import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BOX_STICKER_SIZES, PALLET_STICKER_SIZES, can } from '@pl/domain';
import type { BoxStickerSize, OrgDto, OrgPatch, PalletStickerSize } from '@pl/domain';
import { useId, useState } from 'react';
import { api } from '../../lib/api';
import { stickerInches, stickerMillimetres } from '../../lib/format';
import { qk, useMe, useOrg } from '../../lib/queries';
import { ErrorNote, RefetchNote, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { BlockedPdfLink, openPdf, type OpenedPdf } from '../PdfLink';
import s from './SettingsPrinting.module.css';

type Kind = 'box' | 'pallet';

const sizeLabel = (size: string) => `${stickerInches(size)} · ${stickerMillimetres(size)}`;
const isTall = (size: string) => {
  const [across, down] = size.split('x').map(Number);
  return Number(down) > Number(across);
};

interface TestResult {
  kind: Kind;
  /** The PDF as handed to a tab. */
  pdf: OpenedPdf;
}

/**
 * Settings → Printing (spec §6.1, "Connecting the printer"): the company's box and pallet sticker
 * sizes, and a test label to prove the printer, the size and the alignment before a roll is spent.
 */
export function SettingsPrinting() {
  const me = useMe().data;
  const orgQuery = useOrg();
  const qc = useQueryClient();
  const id = useId();
  const [draft, setDraft] = useState<{ stickerSize?: BoxStickerSize; palletStickerSize?: PalletStickerSize }>({});
  const [saved, setSaved] = useState(false);
  const [test, setTest] = useState<TestResult | null>(null);

  const org = orgQuery.data;
  const boxSize = draft.stickerSize ?? org?.stickerSize;
  const palletSize = draft.palletStickerSize ?? org?.palletStickerSize;
  const changes: OrgPatch = {};
  if (org && draft.stickerSize && draft.stickerSize !== org.stickerSize) changes.stickerSize = draft.stickerSize;
  if (org && draft.palletStickerSize && draft.palletStickerSize !== org.palletStickerSize) changes.palletStickerSize = draft.palletStickerSize;
  const dirty = Object.keys(changes).length > 0;

  const save = useMutation({
    mutationFn: (patch: OrgPatch) => api.patch<OrgDto>('/org', patch),
    onSuccess: (dto) => {
      qc.setQueryData(qk.org, dto);
      // The Stickers page reads the sizes from `me`, so it has to hear about the change too.
      qc.invalidateQueries({ queryKey: qk.org });
      qc.invalidateQueries({ queryKey: qk.me });
      setDraft({});
      setSaved(true);
    },
  });

  const print = useMutation({
    // A test label is not a sticker: it creates no batch and no code, and no scan will receive it
    // (`GET /test-label`). Pressing the button again costs nothing.
    mutationFn: async (kind: Kind): Promise<TestResult> => ({ kind, pdf: await openPdf(`/test-label?kind=${kind}`) }),
    onMutate: () => setTest(null),
    onSuccess: (r) => setTest(r),
  });

  if (!me) return <Spinner label="Loading printing settings" />;
  const canEdit = can(me.role, 'settings');
  const canPrint = can(me.role, 'labels');

  if (orgQuery.isLoadingError)
    return (
      <div className={`panel ${s.prError}`}>
        <ErrorNote error={orgQuery.error} />
        <button type="button" className="btn" onClick={() => orgQuery.refetch()}>
          Try again
        </button>
      </div>
    );
  if (!org || !boxSize || !palletSize) return <Spinner label="Loading printing settings" />;

  function choose(patch: { stickerSize?: BoxStickerSize; palletStickerSize?: PalletStickerSize }) {
    setDraft((d) => ({ ...d, ...patch }));
    setSaved(false);
    save.reset();
  }

  const cards: {
    kind: Kind;
    title: string;
    what: string;
    value: string;
    saved: string;
    options: readonly string[];
    onChange: (v: string) => void;
  }[] = [
    {
      kind: 'box',
      title: 'Box stickers',
      what: 'SKU and blank stickers, one on every box.',
      value: boxSize,
      saved: org.stickerSize,
      options: BOX_STICKER_SIZES,
      onChange: (v) => choose({ stickerSize: v as BoxStickerSize }),
    },
    {
      kind: 'pallet',
      title: 'Pallet stickers',
      what: 'One on every pallet. A 4 × 2 in pallet sticker is laid out across the label.',
      value: palletSize,
      saved: org.palletStickerSize,
      options: PALLET_STICKER_SIZES,
      onChange: (v) => choose({ palletStickerSize: v as PalletStickerSize }),
    },
  ];

  return (
    <div className={s.prPage}>
      <RefetchNote queries={[orgQuery]} />

      <div className="panel">
        <div className="panel-h">
          <h2>Thermal printer</h2>
          <span className="grow" />
          <span className="sub">Set up once on each office laptop</span>
        </div>
        <div className={s.prSetup}>
          <ol className={s.prSteps}>
            <li>Connect the thermal printer to this laptop by USB and install its driver</li>
            <li>In the print dialog choose that printer, scale 100% and no margins</li>
            <li>Chrome remembers the printer, so this is a one-time setup</li>
          </ol>
          <p className={s.prAgent}>
            <Icon name="printer" />
            One-click printing without this dialog needs the print agent, which isn’t built yet.
          </p>
        </div>
      </div>

      <div className={s.prCards}>
        {cards.map((card) => {
          const selectId = `${id}-${card.kind}`;
          const unsaved = card.value !== card.saved;
          return (
            <section key={card.kind} className={`panel ${s.prCard}`} aria-labelledby={`${selectId}-h`}>
              <div className="panel-h">
                <h2 id={`${selectId}-h`}>{card.title}</h2>
                <span className="grow" />
                {unsaved && <span className="chip warn">Not saved</span>}
              </div>
              <div className={s.prBody}>
                <p className={s.prWhat}>{card.what}</p>
                <div className="fld">
                  <label htmlFor={selectId}>{card.kind === 'box' ? 'Box sticker size' : 'Pallet sticker size'}</label>
                  <select id={selectId} className={`inp ${s.prSelect}`} value={card.value} disabled={!canEdit || save.isPending} onChange={(e) => card.onChange(e.target.value)}>
                    {card.options.map((size) => (
                      <option key={size} value={size}>
                        {sizeLabel(size)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={s.prSize}>
                  <span className={`${s.prRatio} ${isTall(card.value) ? s.prRatioTall : ''}`} style={{ aspectRatio: card.value.replace('x', ' / ') }} aria-hidden="true" />
                  <div>
                    <b>{stickerInches(card.value)}</b>
                    <small>{stickerMillimetres(card.value)}</small>
                  </div>
                </div>
              </div>
              {canPrint && (
                <div className={`panel-f ${s.prFoot}`}>
                  <button type="button" className="btn" disabled={unsaved || print.isPending} onClick={() => print.mutate(card.kind)}>
                    <Icon name="printer" />
                    Print test label
                  </button>
                  {unsaved ? (
                    <span className="sub">Save first: the test label prints at the saved size.</span>
                  ) : (
                    <span className="sub">One {card.kind === 'box' ? 'blank box' : 'pallet'} sticker at {stickerInches(card.saved)}</span>
                  )}
                </div>
              )}
              {print.isError && print.variables === card.kind && (
                <div className={s.prNote}>
                  <ErrorNote error={print.error} />
                </div>
              )}
              {test?.kind === card.kind && <TestLine result={test} />}
            </section>
          );
        })}
      </div>

      <div className={`panel ${s.prSave}`}>
        {canEdit ? (
          <>
            {save.isError && <ErrorNote error={save.error} />}
            {saved && !dirty && (
              <p className={s.prOk} role="status">
                Saved. Every sticker printed from now on uses these sizes.
              </p>
            )}
            {!saved && !save.isError && <span className="sub">{dirty ? 'Changes apply to every sticker printed after you save.' : 'Every sticker prints at these sizes.'}</span>}
            <span className="grow" />
            <button type="button" className="btn primary" disabled={!dirty || save.isPending} onClick={() => save.mutate(changes)}>
              <Icon name="check" />
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        ) : (
          <span className="sub">Only an admin can change the sticker sizes.</span>
        )}
      </div>
    </div>
  );
}

function TestLine({ result }: { result: TestResult }) {
  return (
    <p className={`${s.prLine} ${s.prOk}`} role="status">
      Test label ready. It is marked TEST and is not a box. Check the size and alignment before a full run.
      {!result.pdf.opened && (
        <>
          {' '}
          Your browser blocked the new tab. <BlockedPdfLink key={result.pdf.url} pdf={result.pdf} />
        </>
      )}
    </p>
  );
}
